import { useEffect, useState, useMemo } from 'react';
import { useRestQuery } from 'utils/hooks/api';
import { useSelector } from 'react-redux';
import { selectFeatureFlags } from 'core/userContext';
import { isDefined } from 'utils/tsHelpers';
import { List } from '../types';

const LIST_SIZE_ENDPOINT = '/i/lists/getSize';

type ListSizeRequest = {
  listId: number;
};

type ListSizeResponse = {
  size: number;
};

export const useListSizeQuery = (lists: List[]) => {
  const canFetchListSize = !useSelector(selectFeatureFlags).DisableListsSizeQueryFetch;
  const [sizeByListId, setSizeByListId] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);

  const listIdsToFetch = useMemo(() => {
    return lists.map(({ id }) => id).filter((id) => !(id in sizeByListId));
  }, [lists]);

  const currentListId = listIdsToFetch[currentIndex];

  const { isLoading, isSuccess, data } = useRestQuery<ListSizeResponse, ListSizeRequest>(
    LIST_SIZE_ENDPOINT,
    { listId: currentListId },
    { enabled: canFetchListSize && isDefined(currentListId) },
  );

  // Effect 1: Resets index if lists change
  useEffect(() => setCurrentIndex(0), [listIdsToFetch]);

  // Effect 2: Increments index if list successfully loaded
  useEffect(() => {
    if (!isLoading && isSuccess) setCurrentIndex((index) => index + 1);
  }, [isLoading, isSuccess]);

  // Effect 3: Weave current results into cached results
  useEffect(() => {
    if (isSuccess && isDefined(data)) {
      setSizeByListId((prev) => ({ ...prev, [currentListId]: data.size }));
    }
  }, [isSuccess, data]);

  return sizeByListId;
};
